import { useState, useEffect } from 'react';
import { fetchCategoriesWithSDK, fetchCategoriesFromSharetribeAssetDeliveryAPI, getLevel1Categories } from '../util/categoryUtils';

/**
 * Custom hook for managing level 1 categories from Sharetribe
 * @param {Object} options - Configuration options
 * @param {boolean} options.useSDK - Whether to use SDK (default: true) or direct API
 * @returns {Object} Object containing categories, loading state, and error state
 */
export const useCategories = (options = {}) => {
  const { useSDK = true } = options;
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadCategories = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Try SDK first if enabled, then fallback to direct API
        let fetchedCategories = [];
        
        if (useSDK) {
          try {
            fetchedCategories = await fetchCategoriesWithSDK();
          } catch (sdkError) {
            console.warn('SDK approach failed, trying direct API:', sdkError);
            fetchedCategories = await fetchCategoriesFromSharetribeAssetDeliveryAPI();
          }
        } else {
          fetchedCategories = await fetchCategoriesFromSharetribeAssetDeliveryAPI();
        }
        
        // Only get level 1 categories since that's all we use
        const level1Categories = getLevel1Categories(fetchedCategories);
        setCategories(level1Categories);
      } catch (err) {
        console.error('Failed to load categories:', err);
        setError(err.message);
        setCategories([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadCategories();
  }, [useSDK]);

  return {
    categories,           // Level 1 categories only
    isLoading,
    error,
    // Helper function to get categories with "All Categories" option
    getCategoriesWithAllOption: () => [
      { value: "", label: "All Categories" },
      ...categories
    ]
  };
}; 